import { MenuOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Flex,
  Layout,
  Progress,
  theme,
  Typography,
} from "antd";
import SubscriptionPopover from "../popovers/SubscriptionPopover";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MaxAllowedFreeCharts } from "../../constants";
import { useAuthContext } from "../../contexts/auth/useAuthContext";
import { NEW_CHART } from "../../routes";
import { useStore } from "../../store/store";

const { Title } = Typography;
const { Header: AntHeader } = Layout;
const { useToken } = theme;

interface HeaderProps {
  isSmallScreen: boolean;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
}

const Header = ({ isSmallScreen, setDrawerOpen }: HeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { user, hasSubscription } = useAuthContext();
  const chartsCount = useStore((state) => state.chartsCount);

  const handleNewChartClick = () => {
    if (window.location.pathname === NEW_CHART) {
      window.location.reload();
      return;
    }
    navigate(NEW_CHART);
  };
  const maxChartsReached = chartsCount >= MaxAllowedFreeCharts;

  return (
    <AntHeader
      style={{
        background: token.colorBgContainer,
        display: "flex",
        padding: "20px 24px",
        justifyContent: "space-between",
        alignItems: "center",
        alignSelf: "stretch",
      }}
    >
      {/* Left Group: Hamburger (small screens) and Conditional Title */}
      <div style={{ display: "flex", alignItems: "center" }}>
        {isSmallScreen && (
          <Button
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ marginRight: "16px" }}
          />
        )}
        {/* Conditional Title: Hidden if on small screen */}
        {!isSmallScreen && (
          <Title level={3} style={{ marginBottom: 0 }}>
            {t("welcome_name", { name: user?.firstName })}
          </Title>
        )}
      </div>
      <Flex dir="row" gap={16}>
        {!hasSubscription && (
          <ConfigProvider
            theme={{
              token: {
                colorSuccess: token.colorError,
              },
            }}
          >
            <SubscriptionPopover chartsCount={chartsCount}>
              <Progress
                type="circle"
                percent={(chartsCount / MaxAllowedFreeCharts) * 100}
                format={() => `${chartsCount}/${MaxAllowedFreeCharts}`}
                strokeColor={token.colorPrimary}
                size={32}
              />
            </SubscriptionPopover>
          </ConfigProvider>
        )}
        {user && (hasSubscription || !maxChartsReached) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewChartClick}
          >
            {t("new_chart")}
          </Button>
        )}
        {user && !hasSubscription && maxChartsReached && (
          <SubscriptionPopover
            chartsCount={chartsCount}
            showSubscribeButton={false}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/subscription")}
            >
              {t("subscribe_now")}
            </Button>
          </SubscriptionPopover>
        )}
      </Flex>
    </AntHeader>
  );
};

export default Header;
